'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { trackResonance } from '@/lib/bookmarks';

// ─── CLICKABLE VERSE LINK ─────────────────────────────────────────────────────

function VerseLink({ label, href }: { label: string; href: string }) {
  const handleClick = () => {
    trackResonance('verse_link', label, label, 'verse_click').catch(() => {});
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="inline-flex items-center gap-1 transition-all duration-200 border-b"
      style={{ color: 'rgba(196,149,106,0.85)', borderColor: 'rgba(196,149,106,0.3)', fontSize: 'inherit', textDecoration: 'none' }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#D4A82A'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212,168,42,0.6)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.85)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(196,149,106,0.3)'; }}
    >
      <span>{label}</span>
      <span style={{ fontSize: '0.7em', opacity: 0.6 }}>↗</span>
    </a>
  );
}

// ─── NAME CROSS-LINK ──────────────────────────────────────────────────────────

function NameLink({ ar, transliteration, href }: { ar: string; transliteration: string; href: string }) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 transition-all duration-200"
      style={{ border: '1px solid rgba(139,105,20,0.3)', background: 'rgba(139,105,20,0.08)', textDecoration: 'none' }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.18)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(179,138,42,0.5)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.08)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.3)'; }}
    >
      <span style={{ fontFamily: 'serif', fontSize: 13, color: 'rgba(196,149,106,0.9)', direction: 'rtl' }}>{ar}</span>
      <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(196,149,106,0.7)' }}>{transliteration}</span>
    </Link>
  );
}

// ─── ANNOTATED VERSE BLOCK ────────────────────────────────────────────────────

interface AnnotatedVerseProps {
  surah: string;
  ayahRef: string;
  quranUrl: string;
  arabic: string;
  translation: string;
  theme: string;
  etymology: { root: string; meaning: string; note: string }[];
  annotation: string;
  namesActive: { ar: string; transliteration: string; href: string }[];
  arRahmanDuality?: string;
}

function AnnotatedVerse({ surah, ayahRef, quranUrl, arabic, translation, theme, etymology, annotation, namesActive, arRahmanDuality }: AnnotatedVerseProps) {
  const [showEtymology, setShowEtymology] = useState(false);
  const [showAnnotation, setShowAnnotation] = useState(false);

  return (
    <div className="mb-10" style={{ border: '1px solid rgba(44,74,62,0.4)', background: 'rgba(28,43,37,0.35)' }}>
      {/* Verse header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-3" style={{ borderBottom: '1px solid rgba(44,74,62,0.3)' }}>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.5)' }}>{surah}</span>
          <span style={{ color: 'rgba(139,105,20,0.3)', fontSize: 10 }}>·</span>
          <VerseLink label={ayahRef} href={quranUrl} />
        </div>
        <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5" style={{ border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.3)' }}>{theme}</span>
      </div>

      {/* Arabic text */}
      <div className="px-6 py-5 text-center" style={{ borderBottom: '1px solid rgba(44,74,62,0.2)' }}>
        <p style={{ fontFamily: 'serif', fontSize: 'clamp(18px, 3vw, 26px)', color: 'rgba(196,149,106,0.85)', direction: 'rtl', lineHeight: 1.9, marginBottom: 12 }}>
          {arabic}
        </p>
        <p className="font-mono text-sm leading-relaxed italic" style={{ color: 'rgba(245,240,232,0.6)', maxWidth: 680, margin: '0 auto' }}>
          &ldquo;{translation}&rdquo;
        </p>
      </div>

      {/* Ar-Rahman duality note */}
      {arRahmanDuality && (
        <div className="px-6 py-3" style={{ background: 'rgba(139,105,20,0.06)', borderBottom: '1px solid rgba(44,74,62,0.2)' }}>
          <p className="font-mono text-[9px] tracking-widest uppercase mb-1" style={{ color: 'rgba(139,105,20,0.5)' }}>Surah Ar-Rahman Duality</p>
          <p className="font-mono text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>{arRahmanDuality}</p>
        </div>
      )}

      {/* Names active */}
      <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(44,74,62,0.2)' }}>
        <p className="font-mono text-[9px] tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.45)' }}>Names Active in This Verse</p>
        <div className="flex flex-wrap gap-2">
          {namesActive.map((n, i) => (
            <NameLink key={i} ar={n.ar} transliteration={n.transliteration} href={n.href} />
          ))}
        </div>
      </div>

      {/* Expandable: Annotation */}
      <div style={{ borderBottom: '1px solid rgba(44,74,62,0.2)' }}>
        <button
          onClick={() => setShowAnnotation(!showAnnotation)}
          className="w-full flex items-center justify-between px-6 py-3 transition-colors"
          style={{ background: 'transparent' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.15)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.5)' }}>Annotation</span>
          <span className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>{showAnnotation ? '−' : '+'}</span>
        </button>
        {showAnnotation && (
          <div className="px-6 pb-4">
            <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.5)' }}>{annotation}</p>
          </div>
        )}
      </div>

      {/* Expandable: Etymology */}
      <div>
        <button
          onClick={() => setShowEtymology(!showEtymology)}
          className="w-full flex items-center justify-between px-6 py-3 transition-colors"
          style={{ background: 'transparent' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.15)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.5)' }}>Etymological Roots</span>
          <span className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>{showEtymology ? '−' : '+'}</span>
        </button>
        {showEtymology && (
          <div className="px-6 pb-5 flex flex-col gap-3">
            {etymology.map((e, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <span style={{ fontFamily: 'serif', fontSize: 16, color: 'rgba(196,149,106,0.8)', direction: 'rtl', display: 'block' }}>{e.root}</span>
                </div>
                <div>
                  <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.6)' }}>{e.meaning}</span>
                  <p className="font-mono text-[10px] leading-relaxed mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>{e.note}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── VERSE DATA ───────────────────────────────────────────────────────────────

const AYAT_VERSES: AnnotatedVerseProps[] = [
  {
    surah: 'Al-Mulk',
    ayahRef: 'Quran 67:19',
    quranUrl: 'https://quran.com/67/19',
    arabic: 'أَوَلَمْ يَرَوْا إِلَى الطَّيْرِ فَوْقَهُمْ صَافَّاتٍ وَيَقْبِضْنَ ۚ مَا يُمْسِكُهُنَّ إِلَّا الرَّحْمَٰنُ ۚ إِنَّهُ بِكُلِّ شَيْءٍ بَصِيرٌ',
    translation: 'Do they not see the birds above them spreading and folding their wings? None holds them up except Ar-Rahman. He is All-Seeing of everything.',
    theme: 'Ayah as Bridge',
    annotation: 'The bird is not the point — Ar-Rahman holding the bird is the point. The Quran directs the gaze upward (فَوْقَهُمْ — above them) and then immediately names the One who holds. This is the IRL Lens methodology in a single verse: encounter the Zahir (the bird), recognize the Batin (Ar-Rahman\'s active sustenance), arrive at the Haqq (He is All-Seeing). The closing attribute — Al-Basir — is not incidental: the One who holds the bird also sees the one who is looking. The observer is observed.',
    etymology: [
      { root: 'ص-ف-ف', meaning: 'Saffat — spreading in rows', note: 'From the root meaning to arrange in a line or row. The bird\'s outstretched wings are described as an act of ordered arrangement — not random flight but a precise Sunnat process.' },
      { root: 'ق-ب-ض', meaning: 'Yaqbidna — folding/contracting', note: 'From the root meaning to grasp, contract, or take possession. The folding of wings is Al-Qabid (The Contractor) in biological motion — the same Name that contracts provision is active in the bird\'s wingbeat.' },
      { root: 'م-س-ك', meaning: 'Yumsiku — holds/sustains', note: 'From the root meaning to hold, grasp, or prevent from falling. Al-Mumit does not appear here — it is Al-Mumit\'s opposite: the active holding that prevents the fall. Ar-Rahman is named as the Holder, not gravity.' },
    ],
    namesActive: [
      { ar: 'الرَّحْمَن', transliteration: 'Ar-Rahman', href: '/names-reference#ar-rahman' },
      { ar: 'الْبَصِير', transliteration: 'Al-Basir', href: '/names-reference#al-basir' },
      { ar: 'الْقَابِض', transliteration: 'Al-Qabid', href: '/names-reference#al-qabid' },
    ],
    arRahmanDuality: 'Ar-Rahman (mercy/holding) paired with Al-Basir (all-seeing/accountability) — the duality of Surah Ar-Rahman: every mercy is paired with its witness. The bird is held AND seen. The caretaker is sustained AND observed.',
  },
  {
    surah: 'Fussilat',
    ayahRef: 'Quran 41:53',
    quranUrl: 'https://quran.com/41/53',
    arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ حَتَّىٰ يَتَبَيَّنَ لَهُمْ أَنَّهُ الْحَقُّ',
    translation: 'We will show them Our signs in the horizons and within themselves until it becomes clear to them that it is the Truth.',
    theme: 'Dual Register of Signs',
    annotation: 'The Ayat are in the Afaq (آفاق — horizons, the external world) AND in the Anfus (أنفس — selves, the internal world). The IRL Lens operates in both registers simultaneously: the ecological observation (Afaq) and the inner state recognition (Anfus/Haal). The verse uses the future tense (سَنُرِيهِمْ — We WILL show them) — the showing is ongoing, progressive, not complete. The recognition arc is not a single event but a continuous unfolding. Al-Haqq is the destination: the signs accumulate until the Real becomes clear.',
    etymology: [
      { root: 'أ-ف-ق', meaning: 'Afaq — horizons', note: 'Plural of Ufuq (horizon). The root suggests the boundary between earth and sky — the limit of visible space. The Ayat are at the edges of perception, in the furthest reaches of the observable world.' },
      { root: 'ن-ف-س', meaning: 'Anfus — selves/souls', note: 'Plural of Nafs (self/soul). The root connects to breath (nafas) — the self is the breathing thing, the living interior. The Ayat within the self are the Haal-recognition: the inner weather is also a sign.' },
      { root: 'ب-ي-ن', meaning: 'Yatabayyan — becomes clear/distinct', note: 'From the root meaning to be clear, distinct, or separated. The recognition is not sudden illumination but gradual clarification — the Haqq becoming distinct from the noise of the Zahir.' },
    ],
    namesActive: [
      { ar: 'الْحَقّ', transliteration: 'Al-Haqq', href: '/names-reference#al-haqq' },
      { ar: 'الظَّاهِر', transliteration: 'Az-Zahir', href: '/names-reference#az-zahir' },
      { ar: 'الْبَاطِن', transliteration: 'Al-Batin', href: '/names-reference#al-batin' },
    ],
  },
  {
    surah: 'Al-Hashr',
    ayahRef: 'Quran 59:23-24',
    quranUrl: 'https://quran.com/59/23',
    arabic: 'هُوَ اللَّهُ الَّذِي لَا إِلَٰهَ إِلَّا هُوَ الْمَلِكُ الْقُدُّوسُ السَّلَامُ الْمُؤْمِنُ الْمُهَيْمِنُ الْعَزِيزُ الْجَبَّارُ الْمُتَكَبِّرُ ۚ سُبْحَانَ اللَّهِ عَمَّا يُشْرِكُونَ ۝ هُوَ اللَّهُ الْخَالِقُ الْبَارِئُ الْمُصَوِّرُ ۚ لَهُ الْأَسْمَاءُ الْحُسْنَىٰ',
    translation: 'He is Allah, other than whom there is no deity — the Sovereign, the Pure, the Perfection, the Bestower of Faith, the Overseer, the Exalted in Might, the Compeller, the Superior. Exalted is Allah above whatever they associate with Him. He is Allah, the Creator, the Evolver, the Fashioner of Forms. To Him belong the best Names.',
    theme: 'Origin Verse — Most Concentrated Names Passage',
    annotation: 'Quran 59:23-24 is the most concentrated Names passage in the Quran — 14 Names in two verses, culminating in the declaration that all the best Names belong to Him. The sequence moves from sovereignty (Al-Malik) through purification (Al-Quddus) to peace (As-Salam) to the creative triad (Al-Khaliq, Al-Bari, Al-Musawwir). The creative triad is the Leaf Sequence origin: Al-Khaliq (origination), Al-Bari (distinction/evolution), Al-Musawwir (fashioning of form). Every created thing is composed of these three Names in sequence.',
    etymology: [
      { root: 'ق-د-س', meaning: 'Al-Quddus — The Pure/Holy', note: 'From the root meaning absolute purity, freedom from all deficiency. The root appears in Quds (Jerusalem) — the holy city. Al-Quddus is the Name that establishes the infinite distance between the Creator and all created attributes.' },
      { root: 'خ-ل-ق', meaning: 'Al-Khaliq — The Creator', note: 'From the root meaning to create, to measure out, to determine. Al-Khaliq creates from nothing — Huduth Dhati (essential contingency). The root also means to smooth or make smooth — creation as the smoothing of possibility into actuality.' },
      { root: 'ب-ر-أ', meaning: 'Al-Bari — The Evolver/Originator', note: 'From the root meaning to separate, distinguish, or free from. Al-Bari separates each created thing into its distinct form — the evolutionary process of differentiation. The root also appears in Bara\'a (innocence/freedom) — each thing freed into its own distinct nature.' },
      { root: 'ص-و-ر', meaning: 'Al-Musawwir — The Fashioner of Forms', note: 'From the root meaning to form, shape, or image. Al-Musawwir gives the visible form — the Zahir of every created thing. The root appears in Sura (image/form) and Tasawwur (imagination/conception).' },
    ],
    namesActive: [
      { ar: 'الْخَالِق', transliteration: 'Al-Khaliq', href: '/names-reference#al-khaliq' },
      { ar: 'الْبَارِئ', transliteration: 'Al-Bari', href: '/names-reference#al-bari' },
      { ar: 'الْمُصَوِّر', transliteration: 'Al-Musawwir', href: '/names-reference#al-musawwir' },
      { ar: 'الْقُدُّوس', transliteration: 'Al-Quddus', href: '/names-reference#al-quddus' },
      { ar: 'الْمُهَيْمِن', transliteration: 'Al-Muhaymin', href: '/names-reference#al-muhaymin' },
    ],
    arRahmanDuality: 'The Names in 59:23 pair sovereignty with mercy: Al-Jabbar (The Compeller) paired with As-Salam (The Peace-Giver) — power and gentleness as a duality. Al-Mutakabbir (The Superior) paired with Al-Mu\'min (The Bestower of Faith) — transcendence and intimacy. The Surah Ar-Rahman pattern: every attribute of power is paired with an attribute of mercy.',
  },
  {
    surah: 'Ar-Rahman',
    ayahRef: 'Quran 55:1-4',
    quranUrl: 'https://quran.com/55/1',
    arabic: 'الرَّحْمَٰنُ ۝ عَلَّمَ الْقُرْآنَ ۝ خَلَقَ الْإِنسَانَ ۝ عَلَّمَهُ الْبَيَانَ',
    translation: 'Ar-Rahman — taught the Quran — created the human — taught him expression.',
    theme: 'Surah Ar-Rahman Dualities',
    annotation: 'Surah Ar-Rahman opens with the supreme duality framework: Ar-Rahman (mercy) as the first word — the Name that holds all creation. Then four acts in four verses: taught the Quran, created the human, taught expression (Bayan). The sequence is not chronological — it is ontological. The Quran precedes the human because the human was created to receive it. Bayan (expression, articulation, clarity) is the gift that makes the human a carrier of meaning. The IRL Lens is a Bayan-practice: it teaches the human to articulate what the Ayat are saying.',
    etymology: [
      { root: 'ر-ح-م', meaning: 'Ar-Rahman — The Infinitely Merciful', note: 'From the root Rahm (womb) — the mercy that is as intimate and encompassing as a mother\'s womb. Ar-Rahman is the Name that opens the Surah and the Name that holds every bird in the air (67:19). The root connects mercy to the most fundamental act of provision: the womb that sustains before birth.' },
      { root: 'ب-ي-ن', meaning: 'Al-Bayan — Expression/Articulation', note: 'From the root meaning to be clear, distinct, or separated. Bayan is the capacity to make the hidden clear — to articulate what is otherwise inarticulate. The IRL Lens is a Bayan-tool: it gives language to what the Ayat are saying. The root connects to Bayyina (clear evidence) and Tabyin (clarification).' },
    ],
    namesActive: [
      { ar: 'الرَّحْمَن', transliteration: 'Ar-Rahman', href: '/names-reference#ar-rahman' },
      { ar: 'الْعَلِيم', transliteration: 'Al-Alim', href: '/names-reference#al-alim' },
      { ar: 'الْخَالِق', transliteration: 'Al-Khaliq', href: '/names-reference#al-khaliq' },
    ],
    arRahmanDuality: 'The recurring refrain of Surah Ar-Rahman — "فَبِأَيِّ آلَاءِ رَبِّكُمَا تُكَذِّبَانِ" (Then which of the favors of your Lord will you deny?) — is addressed to both humans and jinn: the duality of the addressed. Every blessing in the Surah is paired with accountability. Taqwah is the recognition of both poles.',
  },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function AyatVerse() {
  const [activeVerse, setActiveVerse] = useState(0);

  return (
    <section className="py-24 relative interactive-section" style={{ borderTop: '1px solid rgba(139,105,20,0.2)' }}>
      <div className="max-w-[900px] mx-auto px-6 relative z-10">

        {/* Section header */}
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>Quranic Verse Annotations</p>
          <h2 className="font-mono text-2xl md:text-3xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
            Ayat — Signs in the Horizons
          </h2>
          <p className="font-mono text-xs leading-relaxed max-w-lg mx-auto" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Each verse below is annotated with etymological roots, active Names, and cross-links to the Names Reference. Click verse references to open in Quran.com.
          </p>
        </div>

        {/* Verse selector tabs */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {AYAT_VERSES.map((v, i) => (
            <button
              key={i}
              onClick={() => setActiveVerse(i)}
              className="font-mono text-[9px] tracking-widest uppercase px-3 py-2 transition-all duration-200"
              style={{
                border: `1px solid ${activeVerse === i ? 'rgba(179,138,42,0.6)' : 'rgba(44,74,62,0.4)'}`,
                background: activeVerse === i ? 'rgba(139,105,20,0.15)' : 'transparent',
                color: activeVerse === i ? '#D4A82A' : 'rgba(245,240,232,0.3)',
              }}
            >
              {v.ayahRef}
            </button>
          ))}
        </div>

        {/* Active verse */}
        <AnnotatedVerse {...AYAT_VERSES[activeVerse]} />

        {/* Ayah as Bridge note */}
        <div className="p-8 text-left mb-12" style={{ border: '1px solid rgba(44,74,62,0.4)', background: 'rgba(28,43,37,0.4)' }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>Ayah as Bridge</p>
          <p className="font-mono text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.5)' }}>
            An Ayah is not a destination — it is a bridge. The bird is not the point; Ar-Rahman holding the bird is the point. Everything in creation says Subhanallah, Alhamdulillah — like a tree whose every leaf is praise. The sign points. Follow the pointing.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.4)' }}>Origin Verse:</span>
            <VerseLink label="Quran 59:23-24 — Most concentrated Names passage" href="https://quran.com/59/23" />
          </div>
        </div>

        <div className="text-center">
          <Link
            href="/bridge-irl-application"
            className="font-mono text-xs tracking-widest uppercase px-6 py-3 inline-block transition-all duration-300 hover:opacity-90"
            style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
          >
            Bridge 02: IRL Application →
          </Link>
        </div>
      </div>
    </section>
  );
}
